import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { Observable, forkJoin } from 'rxjs';
import {
    map, switchMap, tap, filter,
} from 'rxjs/operators';

import { getCategory } from '../../../store/category-page.selectors';
import { GridDefault, GridStudio, GridNonDefault, NewCollectionCmsConfig } from '../models/grid.models';
import { CategoryGridService } from '../services/category-grid.service';

import {
    LoadFirstPageNewCollection,
    LoadNewCollectionCategoryProducts,
    LoadNewCollectionCategoryProductsSuccess,
    LoadNextPageGridNewCollectionSuccess,
    LoadNextPageStudioNewCollectionSuccess,
    LoadNextPageStudioNewCollection,
    LoadNextPageGridNewCollection,
    LoadLastPageGridSuccess,
    LoadLastPageStudioSuccess,
} from './category-grid.actions';
import {
    getNewCollectionDefault,
    getNewCollectionStudio,
} from './category-grid.selectors';

import { Category } from '@core/domain/models/category.models';
import { getCmsConfigValue } from '@core/infrastructure/state/core.selectors';
import { ProductsArrayFeaturePublicApi } from '@features/products/productsArray';
import { getGridType } from '@pages/category/store/grid-type/grid-type.selectors';
import { GridTypes } from '@shared/models/grid-type.model';
import { ProductLoaderService } from '@shared/product/services/product-loader-service.impl';
import { ProductPaginationService } from '@shared/product/services/product-pagination.service';

@Injectable()
export class NewCollectionGridEffects {
    private readonly getGridType$: Observable<GridTypes> = this.store.select(getGridType);

    private readonly getGridDefaultNewCollection$: Observable<GridDefault> = this.store.select(getNewCollectionDefault);

    private readonly getGridStudioNewCollection$: Observable<GridStudio> = this.store.select(getNewCollectionStudio);

    private readonly getCategory$: Observable<Category> = this.store.select(getCategory);

    private readonly enableNewCollection$: Observable<NewCollectionCmsConfig[]> = this.store.select(getCmsConfigValue('show_category_products'));

    constructor(
        private readonly actions$: Actions,
        private readonly store: Store,
        private readonly productPaginationService: ProductPaginationService,
        private readonly productsArrayPublicApi: ProductsArrayFeaturePublicApi,
        private readonly productLoaderService: ProductLoaderService,
    ) { }

    // Efecto de inicialización de la Grid de New Collection
    public loadNewCollectionCategories$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(LoadLastPageGridSuccess, LoadLastPageStudioSuccess),
            switchMap(() => this.getCategory$),
            switchMap((category) => {
                return this.enableNewCollection$.pipe(
                    map((enableNewCollection) => {
                        return enableNewCollection?.find(item => item?.IdDestiny?.toString() === category.id);
                    }),
                    filter((newCollection) => !!newCollection?.IdOrigin.length),
                    map((newCollection) => {
                        return LoadNewCollectionCategoryProducts({
                            categories: newCollection.IdOrigin,
                            title: newCollection.TITLE,
                        });
                    }),
                );
            }),
        );
    });

    public loadNewCollectionCategoryProducts$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(LoadNewCollectionCategoryProducts),
            switchMap((action) => {
                return forkJoin(
                    action.categories.map((categoryId) => {
                        return this.productsArrayPublicApi.getCategoryPlatformStudio$(categoryId.toString()).pipe(
                            tap((categoryGrid) => {
                                if (this.store.selectSignal(getCmsConfigValue('GET_CATEGORY_WITH_PRODUCTS'))()) {
                                    this.productLoaderService.addApiProductsToList(categoryGrid.products);
                                }

                                this.productPaginationService.addCategoryProducts(categoryGrid.productIds);
                            }),
                            map((categoryProducts) => {
                                return {
                                    categoryGrids: categoryProducts,
                                    categoryId: categoryId,
                                };
                            }),
                        );
                    })
                );
            }),
            map((data) => LoadNewCollectionCategoryProductsSuccess({ data }))
        );
    });

    public LoadFirstPageNewCollection$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(LoadNewCollectionCategoryProductsSuccess),
            concatLatestFrom(() => this.getGridType$),
            map(([categoryProducts, gridType]) => {
                return CategoryGridService.mapStateGridNewCollection(categoryProducts.data, gridType);
            }),
            map((grid: GridNonDefault) => LoadFirstPageNewCollection({ grid })),
        );
    });

    // Efecto de páginación para la vista por defecto de Id's NEWCOLLECTION
    public loadNextPageGridDefaultNewCollection$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(LoadNextPageGridNewCollection),
            concatLatestFrom(() => [this.getGridDefaultNewCollection$, this.getGridType$]),
            filter(([_, grid]) => grid.currentPage < grid.totalPagesIds),
            map(([_, grid, gridType]) =>
                CategoryGridService.sliceIdsWithPageSize(gridType, grid.idsFiltered, grid.currentPage)),
            map(({ ids, page }) => LoadNextPageGridNewCollectionSuccess({ ids, page }))
        );
    });

    // Efecto de páginación para la vista de GridElements (Templates de Studio) NEWCOLLECTION
    public loadNextPageGridStudioNewCollection$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(LoadNextPageStudioNewCollection),
            concatLatestFrom(() => [this.getGridStudioNewCollection$, this.getGridType$]),
            filter(([_, grid]) => grid.currentPage < grid.totalPagesGridElements),
            map(([_, grid, gridType]) =>
                CategoryGridService.sliceIdsWithPageSize(gridType, grid.gridElementsFiltered, grid.currentPage)),
            map(({ ids, page }) => LoadNextPageStudioNewCollectionSuccess({ ids, page }))
        );
    });
}
